/**
 * Ratios & Rates generator (6.PAFR.2.6).
 *
 * Computes unit rates and solves equivalent-ratio problems with exact rational
 * arithmetic (unitRate / equivalentRatios from the ratio engine). Quantities
 * are chosen first and the answer is derived from them, so the graded answer
 * always matches the numbers in the prompt.
 *
 * Families:
 *   F1: unit rate            (a per b -> a/b per 1)
 *   F2: equivalent ratio     (find the missing value so a:b = c:?)
 *   F3: total from unit rate (rate per unit times a count)
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import { unitRate } from "../engine/ratio";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "ratios-rates" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3";

interface Cfg {
  readonly families: readonly Family[];
  readonly unitMax: number;
  readonly countMax: number;
  readonly scaleMax: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F3"], unitMax: 10, countMax: 6, scaleMax: 4 },
  standard: { families: ["F1", "F2", "F3"], unitMax: 15, countMax: 9, scaleMax: 6 },
  hard: { families: ["F1", "F2", "F3"], unitMax: 20, countMax: 12, scaleMax: 8 },
  challenge: { families: ["F2", "F3"], unitMax: 25, countMax: 15, scaleMax: 10 },
};

const RATE_CONTEXTS: readonly { total: string; per: string; unit: string }[] = [
  { total: "miles", per: "hour", unit: "miles per hour" },
  { total: "dollars", per: "item", unit: "dollars per item" },
  { total: "words", per: "minute", unit: "words per minute" },
  { total: "pages", per: "day", unit: "pages per day" },
];

export class RatiosRatesGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);
      const cxt = rng.pick(RATE_CONTEXTS);

      let answer: Rational;
      let promptText: string;
      let promptLatex: string;
      let steps: { description: string; latex: string }[];

      switch (family) {
        case "F1": {
          // Unit rate: build total as rate * count so the rate is a tidy value.
          const ratePer = rat(rng.int(2, cfg.unitMax));
          const count = rat(rng.int(2, cfg.countMax));
          const total = ratePer.mul(count);
          answer = unitRate(total, count);
          promptText = `A rate is ${total.toString()} ${cxt.total} in ${count.toString()} ${cxt.per}s. What is the unit rate (${cxt.unit})?`;
          promptLatex = `\\frac{${total.toLatex()}\\text{ ${cxt.total}}}{${count.toLatex()}\\text{ ${cxt.per}s}}`;
          steps = [
            {
              description: "Divide the total by the number of units to find the amount per one unit.",
              latex: `${total.toLatex()} \\div ${count.toLatex()} = ${answer.toLatex()}`,
            },
          ];
          break;
        }
        case "F2": {
          // Equivalent ratio a:b = c:? where ? = c * (b/a); build so ? is tidy.
          const a = rat(rng.int(2, cfg.scaleMax));
          const b = rat(rng.int(2, cfg.unitMax));
          const k = rat(rng.int(2, cfg.scaleMax));
          const c = a.mul(k);
          answer = b.mul(k); // ? = c * b / a = k*b
          promptText = `The ratio ${a.toString()} to ${b.toString()} is equivalent to ${c.toString()} to ___. What is the missing value?`;
          promptLatex = `\\frac{${a.toLatex()}}{${b.toLatex()}} = \\frac{${c.toLatex()}}{\\square}`;
          steps = [
            {
              description: "Find the scale factor between the known corresponding terms.",
              latex: `${c.toLatex()} \\div ${a.toLatex()} = ${k.toLatex()}`,
            },
            {
              description: "Multiply the other term by the same scale factor.",
              latex: `${b.toLatex()} \\times ${k.toLatex()} = ${answer.toLatex()}`,
            },
          ];
          break;
        }
        case "F3": {
          // Total from a unit rate: rate per 1 unit times a count.
          const ratePer = rat(rng.int(2, cfg.unitMax));
          const count = rat(rng.int(2, cfg.countMax));
          answer = ratePer.mul(count);
          promptText = `A rate is ${ratePer.toString()} ${cxt.total} per ${cxt.per}. At that rate, how many ${cxt.total} in ${count.toString()} ${cxt.per}s?`;
          promptLatex = `${ratePer.toLatex()} \\times ${count.toLatex()}`;
          steps = [
            {
              description: "Multiply the unit rate by the number of units.",
              latex: `${ratePer.toLatex()} \\times ${count.toLatex()} = ${answer.toLatex()}`,
            },
          ];
          break;
        }
      }

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: promptText, latex: promptLatex },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps,
        hints: [
          { level: 1, text: "A unit rate is the amount for exactly one unit — divide to find it." },
          { level: 2, text: "Equivalent ratios scale both terms by the same factor." },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const ratiosRates = new RatiosRatesGenerator();
